<script setup lang="ts">
    import { ref, computed, watch, onMounted, toRef } from 'vue'
    import { formatStringForDateInput } from "@common/Common"

    import type { AlternativeEditorAttributeFieldReadModel } from "@Alternatives/Alternatives/Domain/readmodels"
    import type { ValidationError } from "@common/types"

    /* props */

    const props = defineProps<{
        propF: AlternativeEditorAttributeFieldReadModel
        propErrors: ValidationError[]
    }>()

    /* shortcuts */

    const f = toRef(props, 'propF')
    const errors = toRef(props, 'propErrors')

    /* derived */

    const error = computed(() =>
        errors.value?.find(e =>
            e.Context.startsWith(`AlternativeAttribute:${f.value.Id}`)
        )?.Error
    )

    const minDate = computed(() => formatStringForDateInput(f.value.MinDate))
    const maxDate = computed(() => formatStringForDateInput(f.value.MaxDate))

    /* local state */

    const value = ref<string | null>(null)

    /* sync back to domain */

    watch(value, (newValue) => {
         f.value.Value = newValue
    })

    /* init */

    onMounted(() =>
    {
        value.value = formatStringForDateInput(f.value.Value)
    })
</script>

<template>
    <div class="form-group">
        <label>
            <span v-if="f.IsRequired" style="color: red;">*</span> {{ f.Name }}
        </label>

        <input type="date"
               class="form-control"
               placeholder="Date..."
               title="Please enter date"
               v-model="value"
               :required="f.IsRequired === true"
               :readonly="f.IsReadonly === true"
               :min="minDate"
               :max="maxDate" />

        <small v-if="minDate || maxDate" class="form-text text-muted">
            <span v-if="minDate">Earliest: {{ minDate }}</span>
            <span v-if="minDate && maxDate"> · </span>
            <span v-if="maxDate">Latest: {{ maxDate }}</span>
        </small>

        <div class="error">{{ error }}</div>

        <small v-if="f.Description" class="form-text text-muted">
            {{ f.Description }}
        </small>
    </div>
</template>
